import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarIcon, BadgePlus, SquarePen } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CustomerFormData } from '@/api/customers';
import { useCreateCustomer, useUpdateCustomer } from '@/hooks/useCustomer';
import { CustomerDTO } from '@/hooks/useCustomer';
import { customerFormSchema, CustomerFormValues } from '@/schemas/customer';
import { Textarea } from '@/components/ui/textarea';
import LoadingCircle from '@/components/LoadingCircle';
import { IEmployee, userStatus } from '@/api/employee';
import { useProfileContext } from '@/hooks/useProfileContext';
import TextUserInactive from '@/components/TextUserInactive';
import { useConfirmSellerChange } from '@/hooks/useConfirmSellerChange';

function CreateUpdate({
  employeesData,
  customer,
}: {
  employeesData: IEmployee[];
  customer?: CustomerDTO;
}) {
  const { profile } = useProfileContext();
  const isSeller = profile?.role?.key === 'marketing';

  const [open, setOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [spouseDatePickerOpen, setSpouseDatePickerOpen] = useState(false);

  const { mutateAsync: createCustomer, isPending } = useCreateCustomer();
  const { mutateAsync: updateCustomer, isPending: isUpdating } =
    useUpdateCustomer();

  const isEdit = !!customer;

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      sellerId: isEdit ? customer?.seller?.id?.toString() : undefined,
      fullName: customer?.fullName || '',
      phoneNumber: customer?.phoneNumber || '',
      phoneFamily: customer?.phoneFamily || '',
      email: customer?.email || '',
      dateOfBirth: customer?.dateOfBirth
        ? new Date(customer.dateOfBirth)
        : undefined,
      gender: customer?.gender || '',
      address: customer?.address || '',
      workAddress: customer?.workAddress || '',
      hobbies: customer?.hobbies || '',
      style: customer?.style || '',
      favoriteColor: customer?.favoriteColor || '',
      notes: customer?.notes || '',
      maritalStatus: customer?.maritalStatus || '',
      spouseName: customer?.spouseName || '',
      spouseBirthday: customer?.spouseBirthday
        ? new Date(customer.spouseBirthday)
        : undefined,
      specialNotes: customer?.specialNotes || '',
    },
    mode: 'all',
  });

  // Watch maritalStatus field for changes
  const maritalStatus = form.watch('maritalStatus');
  const phoneFamily = form.watch('phoneFamily');
  const spouseName = form.watch('spouseName');
  const spouseBirthday = form.watch('spouseBirthday');

  // Clear family fields when maritalStatus is not "married"
  useEffect(() => {
    if (maritalStatus && maritalStatus !== 'married') {
      form.setValue('phoneFamily', '');
      form.setValue('spouseName', '');
      form.setValue('spouseBirthday', null);
      // Clear any existing errors for family fields
      form.clearErrors(['phoneFamily', 'spouseName', 'spouseBirthday']);
    }
  }, [maritalStatus, form]);

  // Trigger validation for family fields when maritalStatus or their values change
  useEffect(() => {
    if (maritalStatus === 'married') {
      if (phoneFamily !== undefined) {
        form.trigger('phoneFamily');
      }
      if (spouseName !== undefined) {
        form.trigger('spouseName');
      }
      if (spouseBirthday !== undefined) {
        form.trigger('spouseBirthday');
      }
    }
  }, [maritalStatus, phoneFamily, spouseName, spouseBirthday, form]);

  useEffect(() => {
    if (customer) {
      form.reset({
        sellerId: customer.seller?.id?.toString() || undefined,
        fullName: customer.fullName || '',
        phoneNumber: customer.phoneNumber || '',
        email: customer.email || '',
        dateOfBirth: customer.dateOfBirth
          ? new Date(customer.dateOfBirth)
          : undefined,
        gender: customer.gender || '',
        address: customer.address || '',
        workAddress: customer.workAddress || '',
        hobbies: customer.hobbies || '',
        style: customer.style || '',
        favoriteColor: customer.favoriteColor || '',
        notes: customer.notes || '',
        maritalStatus: customer.maritalStatus || '',
        spouseName: customer.spouseName || '',
        phoneFamily: customer.phoneFamily || '',
        spouseBirthday: customer.spouseBirthday
          ? new Date(customer.spouseBirthday)
          : undefined,
        specialNotes: customer.specialNotes || '',
      });
    }
  }, [customer, form]);

  useEffect(() => {
    if (isSeller && open) {
      form.setValue('sellerId', profile?.id?.toString());
    }
  }, [isSeller, form, profile?.id, open]);

  const createCustomerPayload = (
    values: CustomerFormValues
  ): CustomerFormData => ({
    sellerId: parseInt(values.sellerId),
    fullName: values.fullName,
    phoneNumber: values.phoneNumber,
    email: values?.email || null,
    dateOfBirth: format(values.dateOfBirth, 'yyyy-MM-dd'),
    gender: values.gender,
    address: values.address || null,
    workAddress: values.workAddress || null,
    hobbies: values.hobbies || null,
    style: values.style || null,
    favoriteColor: values.favoriteColor || null,
    notes: values.notes || null,
    maritalStatus: values.maritalStatus || null,
    spouseName: values.spouseName || null,
    phoneFamily: values.phoneFamily || null,
    spouseBirthday: values.spouseBirthday
      ? format(values.spouseBirthday, 'yyyy-MM-dd')
      : null,
    specialNotes: values.specialNotes || null,
  });

  const { checkAndConfirmSellerChange, ConfirmDialog } = useConfirmSellerChange(
    {
      customer: customer!,
      onConfirm: async () => {
        if (isEdit && form.getValues()) {
          await updateCustomer({
            id: customer!.id,
            data: createCustomerPayload(form.getValues()),
          });
          setOpen(false);
          form.reset();
        }
      },
    }
  );

  async function onSubmit(values: CustomerFormValues) {
    if (isEdit) {
      const canProceed = checkAndConfirmSellerChange(values.sellerId);
      if (!canProceed) {
        return;
      }
      await updateCustomer({
        id: customer!.id,
        data: createCustomerPayload(values),
      });
    } else {
      await createCustomer(createCustomerPayload(values));
    }
    setOpen(false);
    form.reset();
  }

  // Check if family fields should be disabled
  const isFamilyFieldsDisabled = maritalStatus !== 'married';

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          form.reset();
          setOpen(value);
        }}
      >
        <DialogTrigger asChild>
          <Button
            variant={isEdit ? 'outline' : 'default'}
            className={
              isEdit ? 'luxury-button-outline' : 'luxury-button-primary'
            }
          >
            {isEdit ? (
              <>
                <SquarePen className="w-4 h-4" />
                Sửa
              </>
            ) : (
              <>
                <BadgePlus className="w-4 h-4" />
                Thêm mới
              </>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="min-w-[1000px] max-h-[95vh] overflow-y-auto luxury-theme">
          <DialogDescription className="sr-only">
            {isEdit ? 'Sửa khách hàng' : 'Thêm khách hàng'}
          </DialogDescription>
          <DialogHeader className="luxury-header">
            <DialogTitle className="luxury-title">
              {isEdit ? 'Cập nhật thông tin khách hàng' : 'Thêm khách hàng mới'}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <div className="luxury-card">
                <h3 className="luxury-subtitle mb-4">Thông tin cơ bản</h3>
                <div className="flex flex-col gap-6 items-start">
                  <div className="flex gap-6 w-full items-start">
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem className="w-1/3">
                          <Label required className="luxury-label">
                            Tên khách hàng
                          </Label>
                          <FormControl>
                            <Input {...field} className="luxury-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="w-1/3">
                          <Label className="luxury-label">Email</Label>
                          <FormControl>
                            <Input
                              {...field}
                              type="email"
                              className="luxury-input"
                              autoComplete="off"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="sellerId"
                      render={({ field }) => (
                        <FormItem className="w-1/3">
                          <Label required className="luxury-label">
                            Nhân viên phụ trách
                          </Label>
                          <FormControl>
                            <Select
                              value={field.value?.toString() || ''}
                              onValueChange={(value) => field.onChange(value)}
                              disabled={isSeller}
                            >
                              <SelectTrigger className="w-full luxury-input">
                                <SelectValue placeholder="Chọn nhân viên" />
                              </SelectTrigger>
                              <SelectContent className="border-luxury-200">
                                {isSeller ? (
                                  <SelectItem
                                    value={profile?.id?.toString() || ''}
                                  >
                                    {profile?.name}
                                  </SelectItem>
                                ) : (
                                  employeesData
                                    ?.filter(
                                      (employee) =>
                                        employee.status === userStatus.ACTIVE
                                    )
                                    .map((employee) => (
                                      <SelectItem
                                        key={employee.id}
                                        value={employee.id.toString()}
                                        disabled={
                                          employee.status ===
                                          userStatus.INACTIVE
                                        }
                                      >
                                        {employee.name}
                                        <TextUserInactive
                                          status={employee.status}
                                        />
                                      </SelectItem>
                                    ))
                                )}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex gap-6 w-full items-start">
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem className="w-1/3">
                          <Label required className="luxury-label">
                            Số điện thoại
                          </Label>
                          <FormControl>
                            <Input {...field} className="luxury-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <FormItem className="flex flex-col w-1/3">
                          <Label required className="luxury-label">
                            Ngày sinh
                          </Label>
                          <Popover
                            open={datePickerOpen}
                            onOpenChange={setDatePickerOpen}
                          >
                            <PopoverTrigger asChild className="w-full">
                              <FormControl>
                                <Button
                                  variant={'outline'}
                                  className={cn(
                                    'w-full pl-3 text-left font-normal luxury-button-outline bg-white',
                                    !field.value && 'text-muted-foreground'
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, 'dd/MM/yyyy')
                                  ) : (
                                    <span>Chọn ngày sinh</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-full p-0 border-luxury-200"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={(date) => {
                                  field.onChange(date);
                                  setDatePickerOpen(false);
                                }}
                                disabled={(date: Date) => {
                                  const eighteenYearsAgo = new Date();
                                  eighteenYearsAgo.setFullYear(
                                    eighteenYearsAgo.getFullYear() - 18
                                  );
                                  return (
                                    date > eighteenYearsAgo ||
                                    date < new Date('1900-01-01')
                                  );
                                }}
                                initialFocus
                                className="rounded-md border-luxury-200"
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem className="w-1/3">
                          <Label required className="luxury-label">
                            Giới tính
                          </Label>
                          <FormControl>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger className="w-full luxury-input">
                                <SelectValue placeholder="Chọn giới tính" />
                              </SelectTrigger>
                              <SelectContent className="border-luxury-200">
                                <SelectItem value="male">Nam</SelectItem>
                                <SelectItem value="female">Nữ</SelectItem>
                                <SelectItem value="other">Khác</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-amber-200 p-6 shadow-sm">
                <h3 className="text-lg font-medium text-amber-900 mb-4 pb-2 border-b border-amber-100">
                  Thông tin cá nhân
                </h3>
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="workAddress"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label required className="luxury-label">
                          Địa chỉ làm việc
                        </Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label required className="luxury-label">
                          Địa chỉ nhà ở
                        </Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="hobbies"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label className="luxury-label">Sở thích</Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="style"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label className="luxury-label">Phong cách</Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="favoriteColor"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label className="luxury-label">
                          Màu sắc yêu thích
                        </Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="luxury-card">
                <h3 className="luxury-subtitle mb-4">Thông tin gia đình</h3>
                <div className="flex flex-col gap-6">
                  <div className="flex gap-6 w-full items-start">
                    <FormField
                      control={form.control}
                      name="maritalStatus"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <Label className="luxury-label">
                            Tình trạng hôn nhân
                          </Label>
                          <FormControl>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger className="w-full luxury-input">
                                <SelectValue placeholder="Chọn tình trạng" />
                              </SelectTrigger>
                              <SelectContent className="border-luxury-200">
                                <SelectItem value="single">Độc thân</SelectItem>
                                <SelectItem value="married">
                                  Đã kết hôn
                                </SelectItem>
                                <SelectItem value="divorced">Ly hôn</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="spouseName"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <Label
                            {...(maritalStatus === 'married' && {
                              required: true,
                            })}
                            className="luxury-label"
                          >
                            Tên vợ/chồng
                          </Label>
                          <FormControl>
                            <Input
                              {...field}
                              className="luxury-input"
                              disabled={isFamilyFieldsDisabled}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex gap-6 w-full items-start">
                    <FormField
                      control={form.control}
                      name="phoneFamily"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <Label
                            {...(maritalStatus === 'married' && {
                              required: true,
                            })}
                            className="luxury-label"
                          >
                            Số điện thoại vợ/chồng
                          </Label>
                          <FormControl>
                            <Input
                              {...field}
                              className="luxury-input"
                              disabled={isFamilyFieldsDisabled}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="spouseBirthday"
                      render={({ field }) => (
                        <FormItem className="flex flex-col w-1/2">
                          <FormLabel
                            {...(maritalStatus === 'married' && {
                              required: true,
                            })}
                            className="luxury-label"
                          >
                            Ngày sinh vợ/chồng
                          </FormLabel>
                          <Popover
                            open={spouseDatePickerOpen}
                            onOpenChange={setSpouseDatePickerOpen}
                          >
                            <PopoverTrigger asChild className="w-full">
                              <FormControl>
                                <Button
                                  variant={'outline'}
                                  disabled={isFamilyFieldsDisabled}
                                  className={cn(
                                    'w-full pl-3 text-left font-normal luxury-button-outline bg-white',
                                    !field.value && 'text-muted-foreground'
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, 'dd/MM/yyyy')
                                  ) : (
                                    <span>Chọn ngày sinh</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0 border-luxury-200"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value || undefined}
                                onSelect={(date) => {
                                  field.onChange(date);
                                  setSpouseDatePickerOpen(false);
                                }}
                                disabled={(date: Date) => {
                                  const eighteenYearsAgo = new Date();
                                  eighteenYearsAgo.setFullYear(
                                    eighteenYearsAgo.getFullYear() - 18
                                  );
                                  return (
                                    date > eighteenYearsAgo ||
                                    date < new Date('1900-01-01')
                                  );
                                }}
                                initialFocus
                                className="rounded-md border-luxury-200"
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="luxury-card">
                <h3 className="luxury-subtitle mb-4">Ghi chú</h3>
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label className="luxury-label">Ghi chú</Label>
                        <FormControl>
                          <Textarea
                            {...field}
                            className="min-h-[100px] luxury-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="specialNotes"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label className="luxury-label">Ghi chú đặc biệt</Label>
                        <FormControl>
                          <Textarea
                            {...field}
                            className="min-h-[100px] luxury-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full mt-6 luxury-button-primary"
                disabled={isPending || isUpdating}
              >
                {isPending || isUpdating ? (
                  <LoadingCircle />
                ) : (
                  <>{isEdit ? 'Cập nhật thông tin' : 'Tạo mới khách hàng'}</>
                )}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </>
  );
}

export default CreateUpdate;
